import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { getCallDetailsPreScreening, submitCallDetailsPreScreening } from "model/CallModel";

const normalizePreScreeningItems = (rawItems) => {
  const list = Array.isArray(rawItems) ? rawItems : [];
  return list.map((row) => ({
    questionKey: String(row?.questionKey ?? "").trim(),
    existingAnswerId: String(row?.existingAnswerId ?? "").trim(),
    title: String(row?.title ?? "").trim(),
    value: row?.value == null ? "" : String(row.value),
    forFunnl: Boolean(row?.forFunnl),
  }));
};

/** Map list GET envelope (`response.data` = `{ candidateClientId, items }`) like Job Category `response?.data`. */
const mapPreScreeningListResponse = (response) => {
  const data = response?.data;
  if (!data || typeof data !== "object") {
    return { candidateClientId: "", items: [] };
  }
  return {
    candidateClientId: String(data?.candidateClientId ?? "").trim(),
    items: normalizePreScreeningItems(data?.items),
  };
};

/**
 * Same API wiring style as Job Category (`job-category.jsx` + JobCategoryAddEditModal):
 * `getJobCategoryDataById` / `if (response?.data)` / submit `if (response?.success)` + `getJobCategoryList()`.
 */
const PreScreeningModal = ({
  setPreScreeningModalOpen,
  preScreeningModalOpen,
  candidateClientId,
  getCallDetailsPageData,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resolvedCandidateClientId, setResolvedCandidateClientId] = useState("");
  const [items, setItems] = useState([]);

  const closeModal = () => {
    setResolvedCandidateClientId("");
    setItems([]);
    setPreScreeningModalOpen(false);
  };

  useEffect(() => {
    if (!candidateClientId) return undefined;

    let cancelled = false;

    (async () => {
      setLoading(true);
      const id = String(candidateClientId).trim();
      const response = await getCallDetailsPreScreening({ candidateClientId: id });
      if (cancelled) return;
      if (response?.data) {
        const { candidateClientId: fromApi, items: nextItems } = mapPreScreeningListResponse(response);
        setResolvedCandidateClientId(String(fromApi || id).trim());
        setItems(nextItems);
      } else {
        setResolvedCandidateClientId("");
        setItems([]);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [candidateClientId]);

  const handleValueChange = (index, nextValue) => {
    setItems((prev) => {
      const copy = [...prev];
      if (!copy[index]) return prev;
      copy[index] = { ...copy[index], value: nextValue };
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cid = String(resolvedCandidateClientId || candidateClientId || "").trim();
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }

    const params = {
      candidateClientId: cid,
      items: items.map((row) => ({
        questionKey: String(row.questionKey ?? ""),
        existingAnswerId: String(row.existingAnswerId ?? ""),
        title: String(row.title ?? ""),
        value: String(row.value ?? ""),
        forFunnl: Boolean(row.forFunnl),
      })),
    };

    setSaving(true);
    try {
      const response = await submitCallDetailsPreScreening(params);
      if (response?.success) {
        toast.success(response?.message);
        closeModal();
        if (typeof getCallDetailsPageData === "function") {
          getCallDetailsPageData();
        }
      } else {
        toast.error(response?.message || "Unable to save pre-screening");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      closeModal();
    }
  };

  return (
    <Modal
      isOpen={preScreeningModalOpen}
      toggle={handleClose}
      size="lg"
      scrollable
      backdrop="static"
      centered
      className="modal-full-height"
    >
      <ModalHeader toggle={handleClose}>Pre screening</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form id="pre-screening-form" onSubmit={handleSubmit}>
            {!items.length ? (
              <p className="text-muted mb-0">No pre-screening questions returned for this candidate.</p>
            ) : (
              items.map((row, index) => (
                <FormGroup key={`${row.questionKey || "q"}-${row.existingAnswerId || index}`}>
                  <Label className="fw-semibold">{row.title || `Question ${index + 1}`}</Label>
                  <Input
                    type="text"
                    name={`pre-screening-${row.questionKey || index}`}
                    autoComplete="on"
                    className="pre-screening-answer-input"
                    value={row.value}
                    onChange={(event) => handleValueChange(index, event.target.value)}
                    placeholder="Answer"
                  />
                </FormGroup>
              ))
            )}
          </Form>
        )}
      </ModalBody>
      {!loading && (
        <ModalFooter>
          <Button color="secondary" type="button" onClick={handleClose} disabled={saving}>
            Close
          </Button>
          <Button
            color="primary"
            type="submit"
            form="pre-screening-form"
            disabled={saving || !items.length}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default PreScreeningModal;
